import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bot } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { fmtDateTime } from '@/lib/format-date'
import { cardTitle, formCard, pageWide } from '@/lib/form-styles'
import { FeldStatus, feldKlasse, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import {
  operatorApi,
  type AiCentralRoute,
  type AiTaskDefaultRow,
  type AiTaskDefaults,
  type AiTaskDefaultsProvider,
  type AiTaskDefaultsTask,
} from '../api/operator.api'

/** Die allgemeine Vorgabe — gilt für jede Aufgabe ohne eigene Zeile. */
const ALLGEMEIN = 'default'

/** Die Einträge der Server-Umgebung je Weg — die Seite nennt den, der falsch ist. */
const REGION_VARIABLE: Record<AiCentralRoute, string> = { bedrock: 'AI_BEDROCK_REGION', google: 'AI_GOOGLE_REGION' }
const ZUORDNUNG_VARIABLE: Record<AiCentralRoute, string> = { bedrock: 'AI_BEDROCK_MODEL_MAP', google: 'AI_GOOGLE_MODEL_MAP' }

/**
 * AP-43 — **KI-Vorgaben**: Der Betreiber legt je Aufgabe das Modell fest, das für alle Mandanten
 * arbeitet, die selbst nichts gewählt haben. Ein Modellwechsel ist damit ein Handgriff hier, kein Deploy.
 *
 * Speichern nach `.claude/rules/speichern.md`, Fall 1: Die Auswahl speichert sofort, eine Speicherstelle
 * je Zeile. Die erste Option löscht die Zeile — „keine Zeile" heißt „keine Vorgabe".
 */
export default function OperatorKiVorgabenPage() {
  const { t } = useTranslation()
  const [daten, setDaten] = useState<AiTaskDefaults | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setDaten(await operatorApi.getAiTaskDefaults())
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }, [t])

  useEffect(() => {
    void laden()
  }, [laden])

  /** Die Antwort des Speicherns in die Liste: Die allgemeine Vorgabe steht in den Optionen aller anderen Zeilen. */
  const gespeichert = useCallback((zeile: AiTaskDefaultRow) => {
    setDaten((alt) => {
      if (!alt) return alt
      const uebrige = alt.rows.filter((r) => !(r.provider === zeile.provider && r.task === zeile.task))
      return { ...alt, rows: zeile.model === null ? uebrige : [...uebrige, zeile] }
    })
  }, [])

  return (
    <div className={pageWide}>
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <Bot size={22} /> {t('kiVorgaben.titel')}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('kiVorgaben.untertitel')}</p>
      </div>

      {fehler && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      {daten?.via.active && (
        <div className="space-y-1 rounded-md border border-primary/30 bg-muted/20 px-4 py-3 text-sm text-foreground">
          {daten.via.directBlocked ? (
            <p className="text-destructive" role="alert">
              {t('kiVorgaben.direktGesperrt')}
            </p>
          ) : (
            daten.via.routes.length === 0 && <p className="text-destructive">{t('kiVorgaben.keinWeg')}</p>
          )}
          {daten.via.routes.map((weg) => (
            <div key={weg.key} className="space-y-1">
              <p className={weg.ready ? undefined : 'text-destructive'}>
                {t(`kiVorgaben.weg.${weg.key}`, { region: weg.region })}
                {!weg.ready &&
                  ` — ${weg.reason === 'NOT_EU' ? t('kiVorgaben.wegNichtEu', { variable: REGION_VARIABLE[weg.key] }) : t('kiVorgaben.wegNichtBenutzbar')}`}
              </p>
              {!!weg.rejected?.length && (
                <p className="text-destructive" role="alert">
                  {t('kiVorgaben.kennungNichtEu', {
                    variable: ZUORDNUNG_VARIABLE[weg.key],
                    eintraege: weg.rejected.map((e) => `${e.model} → ${e.kennung}`).join(', '),
                  })}
                </p>
              )}
            </div>
          ))}
          {daten.via.routes.length > 1 && <p className="text-xs text-muted-foreground">{t('kiVorgaben.wegReihenfolge')}</p>}
          {daten.via.openaiBlocked && <p className="text-destructive">{t('kiVorgaben.openaiGesperrt')}</p>}
          {daten.via.euOnly && <p className="text-xs text-muted-foreground">{t('kiVorgaben.nurEu')}</p>}
        </div>
      )}

      {daten?.providers.map((anbieter) => (
        <section key={anbieter.provider} className={formCard} aria-label={anbieter.provider}>
          <h2 className={cardTitle}>{t(`kiVorgaben.anbieter.${anbieter.provider}`, anbieter.provider)}</h2>
          <div className="mt-4 divide-y divide-border/60">
            {daten.tasks.map((aufgabe) => (
              <AufgabenZeile
                key={aufgabe.task}
                anbieter={anbieter}
                aufgabe={aufgabe}
                zeile={daten.rows.find((r) => r.provider === anbieter.provider && r.task === aufgabe.task)}
                allgemein={daten.rows.find((r) => r.provider === anbieter.provider && r.task === ALLGEMEIN)?.model ?? null}
                mehrereWege={daten.via.routes.length > 1}
                beiGespeichert={gespeichert}
              />
            ))}
          </div>
          <VerwaisteZeilen anbieter={anbieter} daten={daten} />
        </section>
      ))}
    </div>
  )
}

/** Eine Aufgabe: Bezeichnung, Auswahl des Modells, darunter wer zuletzt geändert hat — rot, wenn die Zeile nicht mehr gilt. */
function AufgabenZeile({
  anbieter,
  aufgabe,
  zeile,
  allgemein,
  mehrereWege,
  beiGespeichert,
}: {
  anbieter: AiTaskDefaultsProvider
  aufgabe: AiTaskDefaultsTask
  zeile: AiTaskDefaultRow | undefined
  allgemein: string | null
  /** Bei mehr als einem zentralen Weg nennt die Option den Weg, der das Modell trägt. */
  mehrereWege: boolean
  beiGespeichert: (zeile: AiTaskDefaultRow) => void
}) {
  const { t } = useTranslation()
  // Fest je Zeile: Die Stelle übernimmt `daten` bei jeder neuen Referenz.
  const leer = useMemo<AiTaskDefaultRow>(() => ({ provider: anbieter.provider, task: aufgabe.task, model: null }), [anbieter.provider, aufgabe.task])
  const stelle = useSpeicherstelle<AiTaskDefaultRow>({
    schluessel: `ai-task-default:${anbieter.provider}:${aufgabe.task}`,
    bezeichnung: `${t('kiVorgaben.titel')}: ${aufgabe.label}`,
    daten: zeile ?? leer,
    schreibe: async (patch, stand) => {
      const antwort = await operatorApi.setAiTaskDefault(anbieter.provider, aufgabe.task, (patch.model as string | null) ?? null, stand || undefined)
      beiGespeichert(antwort)
      return antwort
    },
    werte: (antwort) => antwort as unknown as Datensatz,
    // Eine gelöschte Zeile hat keinen Stand mehr. Ohne das bliebe der alte stehen, und das nächste
    // Setzen liefe in einen Konflikt mit einer Zeile, die es nicht mehr gibt.
    stand: (antwort) => antwort.updatedAt ?? '',
  })
  const modell = useFeld<string | null>(stelle as Speicherstelle<unknown>, 'model', { art: 'auswahl', label: aufgabe.label })

  const istAllgemein = aufgabe.task === ALLGEMEIN
  const ohneZeile = istAllgemein
    ? t('kiVorgaben.codeVorgabe', { modell: anbieter.defaultModel })
    : t('kiVorgaben.wieAllgemein', { modell: allgemein ?? anbieter.defaultModel })
  // Ein Modell, das der Adapter nicht mehr kennt, steht nicht in der Liste — die Zeile soll es trotzdem zeigen.
  const unbekannt = zeile?.model && !anbieter.models.some((m) => m.id === zeile.model) ? zeile.model : null
  const ungueltig = zeile && zeile.valid === false
  const id = `ki-vorgabe-${anbieter.provider}-${aufgabe.task}`

  return (
    <div className="grid gap-x-6 gap-y-1 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(16rem,22rem)] sm:items-start" data-testid={id}>
      <label htmlFor={id} className="pt-1.5 text-sm font-medium text-foreground">
        {aufgabe.label}
      </label>
      <div>
        <select id={id} className={feldKlasse} {...modell.props} aria-invalid={ungueltig || modell.props['aria-invalid']}>
          <option value="">{ohneZeile}</option>
          {unbekannt && <option value={unbekannt}>{unbekannt}</option>}
          {anbieter.models.map((m) => (
            <option key={m.id} value={m.id} disabled={m.route === false}>
              {m.route === false
                ? t('kiVorgaben.nichtZugeordnet', { modell: m.id })
                : mehrereWege && m.route
                  ? t(`kiVorgaben.ueberWeg.${m.route}`, { modell: m.id })
                  : m.id}
            </option>
          ))}
        </select>
        <FeldStatus status={modell} />
        {ungueltig && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {t(`kiVorgaben.grund.${zeile.reason ?? 'MODEL_UNKNOWN'}`, { modell: zeile.model })}
          </p>
        )}
        {zeile?.updatedAt && (
          <p className="mt-1 text-xs text-muted-foreground">
            {zeile.updatedBy
              ? t('kiVorgaben.geaendertVon', { datum: fmtDateTime(zeile.updatedAt), name: zeile.updatedBy })
              : t('kiVorgaben.geaendert', { datum: fmtDateTime(zeile.updatedAt) })}
          </p>
        )}
      </div>
    </div>
  )
}

/** Zeilen, deren Aufgabe der Katalog nicht mehr kennt: Sie wirken nicht, sollen aber nicht unsichtbar liegen bleiben. */
function VerwaisteZeilen({ anbieter, daten }: { anbieter: AiTaskDefaultsProvider; daten: AiTaskDefaults }) {
  const { t } = useTranslation()
  const verwaist = daten.rows.filter((r) => r.provider === anbieter.provider && !daten.tasks.some((a) => a.task === r.task))
  if (!verwaist.length) return null
  return (
    <p className="mt-3 text-xs text-destructive" role="alert">
      {t('kiVorgaben.grund.TASK_UNKNOWN', { aufgaben: verwaist.map((r) => `${r.task} → ${r.model}`).join(', ') })}
    </p>
  )
}
